// The reintroduction plan. Static content: the order of the challenges and
// the shape of one challenge. The words (test food, portions) are in i18n
// under `plan.challenges.<key>`, so both languages stay in step.
//
// The method is the standard one: one FODMAP group at a time, the same test
// food on three days in rising portions, then a few days back on the
// elimination diet before the next group. Portions are common starting points
// from published guidance, not a prescription — the app says so on screen.

export const CHALLENGE_KEYS = [
  'lactose',
  'fructose',
  'sorbitol',
  'mannitol',
  'fructans_wheat',
  'fructans_onion',
  'fructans_garlic',
  'gos',
] as const
export type ChallengeKey = (typeof CHALLENGE_KEYS)[number]

export const CHALLENGE_STATUSES = ['in_progress', 'tolerated', 'limited', 'avoid'] as const
export type ChallengeStatus = (typeof CHALLENGE_STATUSES)[number]

export const PHASES = ['elimination', 'reintroduction', 'personalization'] as const
export type Phase = (typeof PHASES)[number]

/** Test days in one challenge, and rest days before starting the next. */
export const TEST_DAYS = 3
export const WASHOUT_DAYS = 3

/** Symptom scale used everywhere: 0 none … 4 severe. */
export const SCORES = [0, 1, 2, 3, 4] as const
export type Score = (typeof SCORES)[number]

export type ChallengeDay = { day: number; score: Score; note: string }

/** Days as stored (jsonb), tolerating anything malformed. */
export function parseDays(raw: unknown): ChallengeDay[] {
  if (!Array.isArray(raw)) return []
  const out: ChallengeDay[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const { day, score, note } = item as Record<string, unknown>
    if (typeof day !== 'number' || day < 1 || day > TEST_DAYS) continue
    if (typeof score !== 'number' || !SCORES.includes(score as Score)) continue
    out.push({ day, score: score as Score, note: typeof note === 'string' ? note : '' })
  }
  return out.sort((a, b) => a.day - b.day)
}

/** Record (or replace) one test day. */
export function withDay(days: readonly ChallengeDay[], next: ChallengeDay): ChallengeDay[] {
  return [...days.filter((d) => d.day !== next.day), next].sort((a, b) => a.day - b.day)
}

/**
 * A verdict to suggest once the test days are in. The person decides — this
 * only pre-selects the likely answer:
 *   no day above mild            → tolerated
 *   trouble only at the top dose → limited (smaller portions are fine)
 *   trouble from the start       → avoid for now
 */
export function suggestVerdict(days: readonly ChallengeDay[]): Exclude<ChallengeStatus, 'in_progress'> | null {
  if (days.length === 0) return null
  const worst = Math.max(...days.map((d) => d.score))
  if (worst <= 1) return days.length >= TEST_DAYS ? 'tolerated' : null
  const firstBad = days.find((d) => d.score >= 2)!
  return firstBad.day === 1 ? 'avoid' : 'limited'
}

/** How many of the eight challenges have a verdict. */
export function progress(statuses: readonly ChallengeStatus[]): { done: number; total: number } {
  return { done: statuses.filter((s) => s !== 'in_progress').length, total: CHALLENGE_KEYS.length }
}
